import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { Contract } from "./web_parts/types";

/**
 * 管理者セットアップ履歴（contract）の読み取り専用サービス。
 * 書き込みは端末初期セットアップ（createManagerWebAccount）のみ。
 * 1セットアップ = 1ドキュメント（履歴）。
 */
export class ContractService {
  private static normalizeContract(data: Record<string, unknown>, id: string): Contract {
    const setupCountRaw = data.setupCount;
    const lastSetupAtRaw = data.lastSetupAt;
    const createdAtRaw = data.createdAt;
    const updatedAtRaw = data.updatedAt;
    const email = (typeof data.email === "string" && data.email) || "";
    return {
      id,
      userId: typeof data.userId === "string" && data.userId ? data.userId : "",
      email,
      displayName: typeof data.displayName === "string" ? data.displayName : "",
      deviceId: typeof data.deviceId === "string" ? data.deviceId : "",
      setupCount:
        typeof setupCountRaw === "number" && Number.isFinite(setupCountRaw) ? setupCountRaw : 0,
      lastSetupAt:
        typeof lastSetupAtRaw === "number" && Number.isFinite(lastSetupAtRaw)
          ? lastSetupAtRaw
          : typeof updatedAtRaw === "number" && Number.isFinite(updatedAtRaw)
            ? updatedAtRaw
            : typeof createdAtRaw === "number" && Number.isFinite(createdAtRaw)
              ? createdAtRaw
              : 0,
      createdAt:
        typeof createdAtRaw === "number" && Number.isFinite(createdAtRaw) ? createdAtRaw : 0,
    };
  }

  /**
   * 同一メール（無ければ同一 userId）ごとに、最新セットアップのみ isActive=true。
   */
  private static markLatestActive(list: Contract[]): Contract[] {
    const latestKeyToId = new Map<string, { id: string; at: number }>();
    for (const c of list) {
      const key = (c.email || "").trim().toLowerCase() || c.userId || c.id;
      const at = c.lastSetupAt || c.createdAt || 0;
      const prev = latestKeyToId.get(key);
      if (!prev || at > prev.at || (at === prev.at && c.id > prev.id)) {
        latestKeyToId.set(key, { id: c.id, at });
      }
    }
    const activeIds = new Set([...latestKeyToId.values()].map((v) => v.id));
    return list.map((c) => ({
      ...c,
      isActive: activeIds.has(c.id),
    }));
  }

  static async getContracts(): Promise<Contract[]> {
    const snapshot = await getDocs(collection(db, "contract"));
    const list = snapshot.docs.map((d) =>
      this.normalizeContract(d.data() as Record<string, unknown>, d.id)
    );
    list.sort((a, b) => (b.lastSetupAt || b.createdAt) - (a.lastSetupAt || a.createdAt));

    // 旧スキーマで email が無い場合、loginusers から表示用に補完
    await Promise.all(
      list.map(async (c) => {
        if ((c.email && c.displayName) || !c.userId) return;
        try {
          const snap = await getDoc(doc(db, "loginusers", c.userId));
          if (!snap.exists()) return;
          const data = snap.data() as Record<string, unknown>;
          if (!c.email && typeof data.email === "string") c.email = data.email;
          if (!c.displayName && typeof data.displayName === "string") {
            c.displayName = data.displayName;
          }
        } catch {
          // ignore
        }
      })
    );

    return this.markLatestActive(list);
  }
}
